import React, { useState } from 'react'
import { Formik, useFormikContext } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { useHistory } from 'react-router'
import { withCapture, _ } from 'utils'
import ConfirmModal from 'components/ConfirmModal'
import FullPageLoading from 'components/FullPageLoading'
import { SpaceTypeEnum } from 'generated/graphql'
import { useEventSlug } from 'hooks'
import constants from 'constants/constants'
import FileUpload, { IMAGE_ONLY } from 'features/forms/components/FileUpload'
import TextField from '../forms/components/TextField'
import SelectField from '../forms/components/SelectField'
import { createSpace, updateHomeSpace } from './graphql/mutation'
import { showNotification } from '../notifications'
import { showErrorNotification } from '../notifications/notificationsSlice'
import { allSpacesSelector, getEventData } from '../events/eventSlice'

const S = {
    Wrapper: styled.div`
        margin: 0 -10px;
        width: calc(100% + 20px);
    `
}

interface CreateSpaceProps {
  close: () => void;
  onSubmitted?: () => void;
  minOrder?: number
}

interface CreateSpaceModalProps {
  close: () => void;
}

const CreateSpaceForm = () => {
    const spaceTypeOptions = [{
        name: _('space.space_type.map'),
        value: `${SpaceTypeEnum.Map}__1`,
    }, {
        name: _('space.space_type.map.reception'),
        value: `${SpaceTypeEnum.Map}__2`,
    }, {
        name: _('space.space_type.map.floor_plan'),
        value: `${SpaceTypeEnum.Map}__3`,
    }, {
        name: _('space.space_type.map.exhibition_floor'),
        value: `${SpaceTypeEnum.Map}__4`,
    }, {
        name: _('space.space_type.presentation.presentation_room'),
        value: `${SpaceTypeEnum.Presentation}__1`,
    }, {
        name: _('space.space_type.networking.networking_area'),
        value: `${SpaceTypeEnum.Networking}__1`,
    }, {
        name: _('space.space_type.posters.poster_hall'),
        value: `${SpaceTypeEnum.Posters}__1`,
    }, {
        name: _('space.space_type.meeting.meeting_space'),
        value: `${SpaceTypeEnum.Meeting}__1`,
    }, {
        name: _('space.space_type.jobs.jobboard'),
        value: `${SpaceTypeEnum.Jobs}__1`,
    }, {
        name: _('space.space_type.cinema.on_demand_cinema'),
        value: `${SpaceTypeEnum.Cinema}__1`,
    }, {
        name: _('space.space_type.embedded_space'),
        value: `${SpaceTypeEnum.Embedded}`,
    }]

    const spaceTypeOptionsLiteVersion = [{
        name: _('space.space_type.map'),
        value: `${SpaceTypeEnum.Map}`,
    }, {
        name: _('space.space_type.meeting.meeting_space'),
        value: `${SpaceTypeEnum.Meeting}`,
    }, {
        name: _('space.space_type.presentation.presentation_room'),
        value: `${SpaceTypeEnum.Presentation}`,
    }]

    const isLite = constants.isLite
    const { values } = useFormikContext()

    return (
        <S.Wrapper>
            <TextField name="name" label={_('space.form.space_name')} isRequired />
            <SelectField
                name="type"
                label={_('space.form.space_type')}
                options={isLite ? spaceTypeOptionsLiteVersion : spaceTypeOptions}
                optionValue="value"
                optionLabel="name"
                isRequired
                isClearable={false}
            />
            {/* @ts-ignore */}
            {values.type !== SpaceTypeEnum.Embedded && (
                <FileUpload
                    name="image"
                    label={_('space.form.background_image')}
                    accept={IMAGE_ONLY}
                    placeholder={_('space.form.background_image_placeholder')}
                    showLibraryInModal
                />
            )}
        </S.Wrapper>
    )
}

const CreateSpaceModal = ({ close }: CreateSpaceModalProps) => {
    const { submitForm, isValid } = useFormikContext()
    return (
        <>
            <ConfirmModal
                close={close}
                title={_('space.create_dialog.title')}
                confirmDescription={<CreateSpaceForm />}
                confirmText={_('space.new.continue')}
                disabledConfirm={!isValid}
                onBeforeConfirm={submitForm}
                variantConfirm="success"
            />
        </>
    )
}

const CreateSpace = ({ close, onSubmitted, minOrder }: CreateSpaceProps) => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    const history = useHistory()
    const allSpaces = useSelector(allSpacesSelector)
    const [isUploading, setUploading] = useState(false)

    const handleSubmit = withCapture(async ({ name, type, image }) => {
        setUploading(true)
        const submittedType = type.replace(/__.*/, '')
        const response = await createSpace({
            name,
            event: eventSlug,
            type: submittedType,
            timetableOrder: minOrder,
            image
        })
        setUploading(false)
        if (response.data) {
            dispatch(showNotification({
                type: 'success',
                body: _('form.notification.update_success'),
            }))
            if (!allSpaces.length) {
                await updateHomeSpace(eventSlug, response.data?.createSpace?.id)
            }

            onSubmitted?.()
            dispatch(getEventData(eventSlug))
            history.push(`/${eventSlug}/${submittedType}/${response.data?.createSpace?.slug}`)
            return true
        }
        dispatch(showErrorNotification(response.errors))
        return false
    })

    return (
        <Formik
            initialValues={{ name: '', type: SpaceTypeEnum.Map, image: null }}
            validateOnMount
            validate={(values) => {
                const errors: { name?: string, type?: string } = {}
                if (!values.name) {
                    errors.name = _('form.validation.space_name.required')
                }
                if (!values.type) {
                    errors.type = _('form.validation.space_type.required')
                }
                return errors
            }}
            onSubmit={handleSubmit}
        >
            <>
                <CreateSpaceModal close={close} />
                {isUploading && <FullPageLoading isAbsolute fullWidth />}
            </>
        </Formik>
    )
}

export default CreateSpace
